import { supabase } from './supabase';

const SESSION_SCOPED_KEYS: readonly string[] = [
  'universe-auth-cache',
  'currentOrgId',
  'user_geolocation',
  'tenant_id',
  'auth_token',
];

const SESSION_SCOPED_PREFIXES: readonly string[] = [
  'user_geolocation:',
  'simulator_data:',
  'waste_records_',
  'fabrica_waste_records_',
];

type StoreResetFn = () => void;
const storeResetRegistry = new Map<string, StoreResetFn>();

export function registerStoreReset(name: string, fn: StoreResetFn): () => void {
  storeResetRegistry.set(name, fn);
  return () => {
    storeResetRegistry.delete(name);
  };
}

export function clearAuthScopedLocalStorage(): void {
  if (typeof window === 'undefined') return;

  for (const key of SESSION_SCOPED_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Quota / SecurityError — proceed with the rest
    }
  }

  let keysToRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (SESSION_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
  } catch {
    keysToRemove = [];
  }

  for (const key of keysToRemove) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Best effort
    }
  }
}

export async function removeAllSupabaseRealtimeChannels(): Promise<void> {
  try {
    await supabase.removeAllChannels();
  } catch (error) {
    console.warn('Failed to remove Supabase realtime channels:', error);
  }
}

export function broadcastSessionResetToIframes(
  payload: { type: 'SESSION_RESET' | 'SESSION_USER_CHANGED'; userId?: string | null } = {
    type: 'SESSION_RESET',
  }
): void {
  if (typeof document === 'undefined') return;
  const iframes = document.querySelectorAll('iframe');
  iframes.forEach((iframe) => {
    // Target each iframe's exact origin (derived from its own src) instead of
    // broadcasting to '*'. Skip frames whose src cannot be resolved to an origin.
    let targetOrigin: string;
    try {
      targetOrigin = new URL(iframe.src).origin;
    } catch {
      return;
    }
    try {
      iframe.contentWindow?.postMessage(payload, targetOrigin);
    } catch {
      // Cross-origin restrictions or no contentWindow — ignore
    }
  });
}

function resetRegisteredStores(): void {
  for (const [name, fn] of storeResetRegistry.entries()) {
    try {
      fn();
    } catch (error) {
      console.warn(`Failed to reset store "${name}":`, error);
    }
  }
}

export interface SessionResetOptions {
  reason: string;
  isUserChange: boolean;
  newUserId?: string | null;
}

export async function resetSession(options: SessionResetOptions): Promise<void> {
  await removeAllSupabaseRealtimeChannels();
  resetRegisteredStores();
  clearAuthScopedLocalStorage();
  broadcastSessionResetToIframes(
    options.isUserChange
      ? { type: 'SESSION_USER_CHANGED', userId: options.newUserId ?? null }
      : { type: 'SESSION_RESET' }
  );
}

export const __testing = {
  SESSION_SCOPED_KEYS,
  SESSION_SCOPED_PREFIXES,
  storeResetRegistry,
};
